import {
  keepPreviousData,
  type QueryClient,
  type QueryKey,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import {
  emailSignaturesApi,
  emailTemplatesApi,
  mailboxesApi,
  smsTemplatesApi
} from "./api";
import type {
  CreateEmailTemplatePayload,
  SaveEmailSignaturePayload,
  SendTestEmailPayload,
  SmsTemplatePayload,
  UpdateEmailTemplatePayload
} from "./types";

const mailboxKeys = {
  all: ["MAILBOXES"],
  lists: () => [...mailboxKeys.all, "LIST"],
  list: (filters?: Record<string, unknown>) => [
    ...mailboxKeys.lists(),
    filters
  ],
  details: () => [...mailboxKeys.all, "DETAIL"],
  detail: (id: string) => [...mailboxKeys.details(), id]
};

export const useMailboxes = (enabled: boolean = true) =>
  useQuery({
    queryKey: mailboxKeys.lists(),
    queryFn: () => mailboxesApi.list(),
    enabled
  });

// CRM-1165: the account-wide "Make default" toggle is gone — a mailbox is the
// default for the properties explicitly assigned to it.
export const useMailboxPropertyDefaults = (mailboxId: string | null) =>
  useQuery({
    queryKey: [...mailboxKeys.detail(mailboxId ?? ""), "PROPERTY_DEFAULTS"],
    queryFn: () => mailboxesApi.getPropertyDefaults(mailboxId as string),
    enabled: Boolean(mailboxId),
    // Never served from cache: the picker seeds once on open and writes back a
    // REPLACE, and a cached failure would lock the user out of the mailbox.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    retry: 1
  });

export const useSetMailboxPropertyDefaults = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["MAILBOXES", "SET_PROPERTY_DEFAULTS"],
    mutationFn: ({
      mailboxId,
      propertyIds
    }: {
      mailboxId: string;
      propertyIds: number[];
    }) => mailboxesApi.setPropertyDefaults(mailboxId, propertyIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mailboxKeys.all });
      // The property page and inbox composer keep their own cache of the sender.
      queryClient.invalidateQueries({ queryKey: ["PROPERTY_DEFAULT_EMAIL"] });
    },
    onError: (error: unknown) => {
      toast.error(
        getErrorMessage(error, "Failed to update default properties")
      );
    }
  });
};

// SMS Templates

type SmsTemplateListFilters = {
  search?: string;
  order_direction?: "asc" | "desc";
  order_by?: "updated_at" | "name";
  favorites_first?: boolean;
};

const smsTemplateKeys = {
  all: ["SMS_TEMPLATES"],
  lists: () => [...smsTemplateKeys.all, "LIST"],
  list: (page: number, perPage: number, filters: SmsTemplateListFilters) => [
    ...smsTemplateKeys.lists(),
    page,
    perPage,
    filters
  ],
  variables: (subjectType: string) => [
    ...smsTemplateKeys.all,
    "VARIABLES",
    subjectType
  ],
  details: () => [...smsTemplateKeys.all, "DETAIL"],
  detail: (id: number) => [...smsTemplateKeys.details(), id]
};

export const useSmsTemplates = (
  page = 1,
  perPage = 25,
  filters: SmsTemplateListFilters = {}
) =>
  useQuery({
    queryKey: smsTemplateKeys.list(page, perPage, filters),
    queryFn: () =>
      smsTemplatesApi.list(
        page,
        perPage,
        filters.search,
        filters.order_direction,
        filters.order_by,
        filters.favorites_first
      ),
    placeholderData: keepPreviousData
  });

export const useSmsTemplate = (id: number) =>
  useQuery({
    queryKey: smsTemplateKeys.detail(id),
    queryFn: () => smsTemplatesApi.get(id),
    enabled: id > 0
  });

export const useSmsVariables = (subjectType = "lead") =>
  useQuery({
    queryKey: smsTemplateKeys.variables(subjectType),
    queryFn: () => smsTemplatesApi.variables(subjectType)
  });

export const useCreateSmsTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["SMS_TEMPLATES", "CREATE"],
    mutationFn: (payload: SmsTemplatePayload) =>
      smsTemplatesApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: smsTemplateKeys.lists() });
    }
  });
};

export const useUpdateSmsTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["SMS_TEMPLATES", "UPDATE"],
    mutationFn: ({
      id,
      payload
    }: {
      id: number;
      payload: Partial<SmsTemplatePayload>;
    }) => smsTemplatesApi.update(id, payload),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: smsTemplateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: smsTemplateKeys.detail(id) });
    }
  });
};

// Puts a Boom default template back to the wording Boom seeded it with (CRM-1644).
export const useResetSmsTemplateToStandard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["SMS_TEMPLATES", "UPDATE"],
    mutationFn: (id: number) => smsTemplatesApi.resetToStandard(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: smsTemplateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: smsTemplateKeys.detail(id) });
    }
  });
};

export const useDuplicateSmsTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["SMS_TEMPLATES", "DUPLICATE"],
    mutationFn: (id: number) => smsTemplatesApi.duplicate(id),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: smsTemplateKeys.lists() });
      toast.success("SMS template duplicated");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to duplicate SMS template"));
    }
  });
};

export const useDeleteSmsTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["SMS_TEMPLATES", "DELETE"],
    mutationFn: (id: number) => smsTemplatesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: smsTemplateKeys.lists() });
    }
  });
};

// Favorites (CRM-1882): the star flips in every cached list right away; the
// Templates list keeps its groups until it is opened again.
type FavoriteVariables = { id: number; favorite: boolean };

const setFavoriteInLists = (
  queryClient: QueryClient,
  queryKey: QueryKey,
  { id, favorite }: FavoriteVariables
) =>
  queryClient.setQueriesData<{ items: { id: number }[] }>(
    { queryKey },
    (data) =>
      data?.items
        ? {
            ...data,
            items: data.items.map((item) =>
              item.id === id ? { ...item, favorite } : item
            )
          }
        : data
  );

const useSetTemplateFavorite = ({
  allKey,
  listsKey,
  setFavorite
}: {
  allKey: QueryKey;
  listsKey: QueryKey;
  setFavorite: (id: number, favorite: boolean) => Promise<unknown>;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...allKey, "FAVORITE", "UPDATE"],
    mutationFn: ({ id, favorite }: FavoriteVariables) =>
      setFavorite(id, favorite),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: listsKey });
      setFavoriteInLists(queryClient, listsKey, variables);
    },
    onError: (error, variables) => {
      setFavoriteInLists(queryClient, listsKey, {
        ...variables,
        favorite: !variables.favorite
      });
      toast.error(getErrorMessage(error, "Failed to update favorites"));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: listsKey });
    }
  });
};

export const useSetSmsTemplateFavorite = () =>
  useSetTemplateFavorite({
    allKey: smsTemplateKeys.all,
    listsKey: smsTemplateKeys.lists(),
    setFavorite: smsTemplatesApi.setFavorite
  });

// Email Templates

const emailTemplateKeys = {
  all: ["EMAIL_TEMPLATES"],
  lists: () => [...emailTemplateKeys.all, "LIST"],
  list: (params?: Record<string, unknown>) => [
    ...emailTemplateKeys.lists(),
    params
  ],
  details: () => [...emailTemplateKeys.all, "DETAIL"],
  detail: (id: number) => [...emailTemplateKeys.details(), id],
  variables: (subjectType: string) => [
    ...emailTemplateKeys.all,
    "VARIABLES",
    subjectType
  ]
};

export const useEmailTemplates = (params?: Record<string, unknown>) =>
  useQuery({
    queryKey: emailTemplateKeys.list(params),
    queryFn: () => emailTemplatesApi.list(params),
    placeholderData: keepPreviousData
  });

export const useEmailTemplate = (id: number) =>
  useQuery({
    queryKey: emailTemplateKeys.detail(id),
    queryFn: () => emailTemplatesApi.get(id),
    enabled: id > 0
  });

// Puts a Boom default email back to the subject and wording Boom seeded it with (CRM-1644).
export const useResetEmailTemplateToStandard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["EMAIL_TEMPLATES", "UPDATE"],
    mutationFn: (id: number) => emailTemplatesApi.resetToStandard(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.detail(id) });
    }
  });
};

export const useEmailVariables = (subjectType = "lead") =>
  useQuery({
    queryKey: emailTemplateKeys.variables(subjectType),
    queryFn: () => emailTemplatesApi.variables(subjectType),
    staleTime: Infinity
  });

export const useCreateEmailTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...emailTemplateKeys.all, "CREATE"],
    mutationFn: (payload: CreateEmailTemplatePayload) =>
      emailTemplatesApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: emailTemplateKeys.lists()
      });
      // The template's signature counts on that signature's "Used on".
      queryClient.invalidateQueries({ queryKey: ["EMAIL_SIGNATURES"] });
      toast.success("Email template created");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to create email template"));
    }
  });
};

export const useUpdateEmailTemplate = (
  id: number,
  options?: { successMessage?: string }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...emailTemplateKeys.all, "UPDATE"],
    mutationFn: (payload: UpdateEmailTemplatePayload) =>
      emailTemplatesApi.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: emailTemplateKeys.lists()
      });
      queryClient.invalidateQueries({ queryKey: ["EMAIL_SIGNATURES"] });
      queryClient.invalidateQueries({
        queryKey: emailTemplateKeys.detail(id)
      });
      toast.success(options?.successMessage ?? "Email template updated");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to update email template"));
    }
  });
};

const recipientCount = (count: number) =>
  `${count} ${count === 1 ? "recipient" : "recipients"}`;

export const useSendTestEmail = (id: number) =>
  useMutation({
    mutationKey: [...emailTemplateKeys.all, "SEND_TEST"],
    mutationFn: (payload: SendTestEmailPayload) =>
      emailTemplatesApi.sendTest(id, payload),
    onSuccess: ({ sent_count, failed_count }) => {
      toast.success(`Emails sent to ${recipientCount(sent_count)}`);
      if (failed_count > 0)
        toast.error(
          `Failed to send test email to ${recipientCount(failed_count)}. Please try again later`
        );
    },
    onError: () => {
      toast.error("Failed to send test email. Please try again later");
    }
  });

export const useDuplicateEmailTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...emailTemplateKeys.all, "DUPLICATE"],
    mutationFn: (id: number) => emailTemplatesApi.duplicate(id),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: emailTemplateKeys.lists() });
      toast.success("Email template duplicated");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to duplicate email template"));
    }
  });
};

export const useSetEmailTemplateFavorite = () =>
  useSetTemplateFavorite({
    allKey: emailTemplateKeys.all,
    listsKey: emailTemplateKeys.lists(),
    setFavorite: emailTemplatesApi.setFavorite
  });

export const useDeleteEmailTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...emailTemplateKeys.all, "DELETE"],
    mutationFn: (id: number) => emailTemplatesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: emailTemplateKeys.lists()
      });
      toast.success("Email template deleted");
    },
    onError: () => {
      toast.error("Failed to delete email template");
    }
  });
};

// Email signatures (CRM-1705)

const emailSignatureKeys = {
  all: ["EMAIL_SIGNATURES"],
  lists: () => [...emailSignatureKeys.all, "LIST"],
  list: (params?: Record<string, unknown>) => [
    ...emailSignatureKeys.lists(),
    params
  ],
  details: () => [...emailSignatureKeys.all, "DETAIL"],
  detail: (id: number) => [...emailSignatureKeys.details(), id],
  templates: () => [...emailSignatureKeys.all, "TEMPLATES"]
};

// Companies keep a handful of signatures, so the pickers load them in one page.
const ALL_SIGNATURES = { page: 1, per_page: 100 };

export const useEmailSignatures = (params?: Record<string, unknown>) =>
  useQuery({
    queryKey: emailSignatureKeys.list(params),
    queryFn: () => emailSignaturesApi.list(params)
  });

export const useAllEmailSignatures = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: emailSignatureKeys.list(ALL_SIGNATURES),
    queryFn: () => emailSignaturesApi.list(ALL_SIGNATURES),
    select: (data) => data.items,
    enabled: options?.enabled ?? true
  });

export const useEmailSignature = (id: number | null | undefined) =>
  useQuery({
    queryKey: emailSignatureKeys.detail(id ?? 0),
    queryFn: () => emailSignaturesApi.get(id ?? 0),
    enabled: Boolean(id)
  });

export const useSignatureTemplates = () =>
  useQuery({
    queryKey: emailSignatureKeys.templates(),
    queryFn: () => emailSignaturesApi.templates()
  });

// A signature change moves templates between signatures, so every list that
// shows a template's signature is refreshed.
const invalidateSignatureUsage = (
  queryClient: ReturnType<typeof useQueryClient>
) => {
  queryClient.invalidateQueries({ queryKey: emailSignatureKeys.all });
  queryClient.invalidateQueries({ queryKey: emailTemplateKeys.all });
  queryClient.invalidateQueries({ queryKey: ["SYSTEM_EMAILS"] });
};

export const useSaveEmailSignature = (id?: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...emailSignatureKeys.all, id ? "UPDATE" : "CREATE"],
    mutationFn: (payload: SaveEmailSignaturePayload) =>
      id
        ? emailSignaturesApi.update(id, payload)
        : emailSignaturesApi.create(payload),
    onSuccess: () => {
      invalidateSignatureUsage(queryClient);
      toast.success("Signature saved");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to save signature"));
    }
  });
};

export const useDuplicateEmailSignature = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...emailSignatureKeys.all, "DUPLICATE"],
    mutationFn: (id: number) => emailSignaturesApi.duplicate(id),
    onSuccess: (copy) => {
      queryClient.refetchQueries({ queryKey: emailSignatureKeys.lists() });
      toast.success(`Duplicated as "${copy.name}"`);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to duplicate signature"));
    }
  });
};

export const useDeleteEmailSignature = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...emailSignatureKeys.all, "DELETE"],
    mutationFn: (id: number) => emailSignaturesApi.delete(id),
    onSuccess: () => {
      invalidateSignatureUsage(queryClient);
      toast.success("Signature deleted");
    },
    onError: () => {
      toast.error("Failed to delete signature");
    }
  });
};
